// OPEN OEM ORDERS -- Order Management O7 (owner decisions D181 / D184,
// 2026-10-30).
//
// Every OEM order not yet delivered and not cancelled that the caller may see:
// Admin every quote's, Sales their OWN quotes', Viewer what the quote rules
// allow. ⚠️ THE SERVER SCOPES AND FILTERS (open_oem_orders); this page only
// sends the filters and shows the answer. Status, late and at risk are the
// server's, derived from recorded events.
//
// READ ONLY. Events are recorded on the quote screen, where the order's quote
// and its lines are in view; each row links there.

import { Fragment, useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getOpenOemOrders, type OemOrder } from "@/services/api";
import { formatDate, formatTimestampDate } from "@/lib/format";
import { FlagBadges, OrderEvents, OrderProgressLine, StatusPill, STATUS_LABEL } from "@/components/OemOrderStatus";
import { HowItWorks } from "@/components/DealProgress";
import { orderNextText } from "@/lib/dealProgress";
import { CustomerFlagBadges } from "@/components/CustomerDates";
import { semanticColors, radius, spacing, heights, typography } from "@/styles/tokens";

const INPUT: React.CSSProperties = {
  height: heights.input, padding: `0 ${spacing[8]}px`,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  fontSize: typography.fontSize.base, backgroundColor: semanticColors.surface,
  color: semanticColors.textPrimary, boxSizing: "border-box",
};
const BUTTON: React.CSSProperties = {
  height: heights.input, padding: `0 ${spacing[10]}px`, border: `1px solid ${semanticColors.border}`,
  borderRadius: radius.base, fontSize: typography.fontSize.sm, backgroundColor: semanticColors.surface,
  color: semanticColors.textSecondary, cursor: "pointer", whiteSpace: "nowrap",
};
const NOTE: React.CSSProperties = { fontSize: typography.fontSize.sm, color: semanticColors.textSecondary };
const TH: React.CSSProperties = {
  textAlign: "left", padding: `${spacing[6]}px ${spacing[8]}px`,
  fontSize: typography.fontSize.xs, color: semanticColors.textMuted,
  fontWeight: typography.fontWeight.medium, borderBottom: `1px solid ${semanticColors.border}`,
  whiteSpace: "nowrap", backgroundColor: semanticColors.surface,
};
const TD: React.CSSProperties = {
  padding: `${spacing[6]}px ${spacing[8]}px`, fontSize: typography.fontSize.base,
  borderBottom: `1px solid ${semanticColors.border}`, verticalAlign: "top",
};
const OPEN_STATUSES = ["placed", "acknowledged", "partially_shipped", "shipped", "partially_delivered"] as const;

/** The earliest promise still standing on a line not yet delivered. */
function nextPromise(o: OemOrder): string | null {
  const dates = o.lines
    .filter((l) => (l.delivered_qty ?? 0) < l.qty_ordered)
    .map((l) => l.current_promised_date ?? l.promised_date)
    .filter((d): d is string => !!d)
    .sort();
  return dates[0] ?? null;
}

export default function OemOrdersOpenPage() {
  const [rows, setRows] = useState<OemOrder[] | null>(null);
  const [error, setError] = useState("");
  const [oem, setOem] = useState("");
  const [status, setStatus] = useState("");
  const [late, setLate] = useState(false);
  const [atRisk, setAtRisk] = useState(false);
  const [customerRisk, setCustomerRisk] = useState(false);
  const [open, setOpen] = useState<number | null>(null);

  const load = useCallback(async () => {
    const r = await getOpenOemOrders({ oem: oem.trim() || undefined, status: status || undefined, late, atRisk, customerRisk });
    // Fail loud: a failed read clears the rows rather than leaving stale ones.
    if (!r.ok) { setRows(null); setError(r.error ?? "The OEM orders could not be read."); return; }
    setError(""); setRows(r.orders ?? []);
  }, [oem, status, late, atRisk, customerRisk]);
  useEffect(() => {
    const t = setTimeout(() => { void load(); }, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div data-testid="oem-orders-page" style={{ padding: spacing[24] }}>
      <div style={{ marginBottom: spacing[16] }}>
        <h1 style={{
          margin: 0, fontSize: typography.fontSize.xxl, fontWeight: typography.fontWeight.semibold,
          color: semanticColors.textPrimary, letterSpacing: typography.letterSpacing.tight,
        }}>Open OEM orders</h1>
        <div style={{ fontSize: typography.fontSize.base, color: semanticColors.textSecondary }}>
          Orders placed with our suppliers that are not yet delivered or cancelled. Status comes from what has been
          recorded; record events on the quote.
        </div>
      </div>

      <HowItWorks storageKey="anixify.howItWorks.oemOrders" testId="oo-how">
        <div>OEM = the manufacturer or supplier we buy from. An OEM order is placed from a Won quote (step 5 on the
          quote screen) and follows: Placed → Acknowledged → Shipped → Delivered.</div>
        <div>You record each step on the quote, when the OEM tells you (ack number, AWB, invoice). Late = past the date
          the OEM promised. At risk = no acknowledgement within 2 working days, or within 3 days of the promise.
          A delivered or cancelled order leaves this list.</div>
      </HowItWorks>

      <div style={{
        display: "flex", alignItems: "center", gap: spacing[12], flexWrap: "wrap", minHeight: heights.filterBar,
        padding: `0 ${spacing[12]}px`, backgroundColor: semanticColors.panelBg,
        border: `1px solid ${semanticColors.border}`, borderRadius: radius.base, marginBottom: spacing[8],
      }}>
        <input data-testid="oo-filter-oem" placeholder="OEM" aria-label="OEM" style={{ ...INPUT, width: 200 }}
          value={oem} onChange={(e) => setOem(e.target.value)} />
        <select data-testid="oo-filter-status" aria-label="Status" style={INPUT} value={status}
          onChange={(e) => setStatus(e.target.value)}>
          <option value="">Any open status</option>
          {OPEN_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
        </select>
        <label style={{ ...NOTE, display: "flex", gap: spacing[4], alignItems: "center" }}>
          <input type="checkbox" data-testid="oo-filter-late" checked={late} onChange={(e) => setLate(e.target.checked)} /> Late
        </label>
        <label style={{ ...NOTE, display: "flex", gap: spacing[4], alignItems: "center" }}>
          <input type="checkbox" data-testid="oo-filter-risk" checked={atRisk} onChange={(e) => setAtRisk(e.target.checked)} /> At risk
        </label>
        <label style={{ ...NOTE, display: "flex", gap: spacing[4], alignItems: "center" }}>
          <input type="checkbox" data-testid="oo-filter-customer" checked={customerRisk}
            onChange={(e) => setCustomerRisk(e.target.checked)} /> Customer date at risk
        </label>
      </div>

      {error ? (
        <div role="alert" data-testid="oo-error" style={{ ...NOTE, color: semanticColors.destructive }}>
          {error} <button type="button" style={BUTTON} onClick={() => { void load(); }}>Retry</button>
        </div>
      ) : rows === null ? <div style={NOTE}>Loading…</div> : (
        <>
          <div data-testid="oo-count" style={{ ...NOTE, marginBottom: spacing[8], fontVariantNumeric: "tabular-nums" }}>
            {rows.length} open OEM order{rows.length === 1 ? "" : "s"}
          </div>
          {rows.length === 0 ? (
            <div data-testid="oo-empty" style={{ ...NOTE, padding: spacing[16], border: `1px dashed ${semanticColors.border}`, borderRadius: radius.base }}>
              {oem.trim() || status || late || atRisk || customerRisk
                ? "No open OEM order matches these filters. Clear a filter to see more."
                : "No open OEM orders. Orders appear here once they are placed from a Won quote, and leave when delivered or cancelled."}
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr>{["Number", "Quote", "Customer", "OEM", "Status", "Lines", "Next promise", "Placed", ""].map((h, i) => (
                  <th key={h || i} style={TH}>{h}</th>))}</tr></thead>
                <tbody>
                  {rows.map((o) => {
                    const next = nextPromise(o);
                    return (
                      <Fragment key={o.id}>
                        <tr data-testid={`oo-row-${o.id}`}>
                          <td style={{ ...TD, fontWeight: typography.fontWeight.medium, whiteSpace: "nowrap" }}>{o.number}</td>
                          <td style={{ ...TD, whiteSpace: "nowrap" }}><Link to={`/saved-quotes/${o.quote_id}`}>{o.quote_name}</Link></td>
                          <td style={TD}>{o.customer ?? "—"}</td>
                          <td style={TD}>{o.oem}</td>
                          <td style={TD}>
                            <span style={{ display: "inline-flex", gap: spacing[4], flexWrap: "wrap" }}>
                              <StatusPill status={o.status} testId={`oo-status-${o.id}`} />
                              <FlagBadges late={o.late} atRisk={o.at_risk} reasons={o.flag_reasons} testId={`oo-flags-${o.id}`} />
                              <CustomerFlagBadges promiseAfter={o.promise_after_customer} atRisk={o.customer_date_at_risk}
                                reasons={o.customer_reasons} testId={`oo-cflags-${o.id}`} />
                            </span>
                            <div style={{ marginTop: spacing[4] }}><OrderProgressLine order={o} testId={`oo-progress-${o.id}`} /></div>
                            <div data-testid={`oo-next-${o.id}`} style={{ ...NOTE, fontSize: typography.fontSize.xs, marginTop: spacing[2] }}>
                              {orderNextText(o)}
                            </div>
                          </td>
                          <td style={TD}>{o.lines.map((l) => `${l.part_number} × ${l.qty_ordered}`).join(", ")}</td>
                          <td style={{ ...TD, whiteSpace: "nowrap" }}>{next ? formatDate(next) : "Not stated"}</td>
                          <td style={{ ...TD, whiteSpace: "nowrap", color: semanticColors.textSecondary }}>
                            {o.placed_at ? formatTimestampDate(o.placed_at) : "—"}
                          </td>
                          <td style={TD}>
                            <button type="button" data-testid={`oo-open-${o.id}`} aria-expanded={open === o.id} style={BUTTON}
                              onClick={() => setOpen(open === o.id ? null : o.id)}>{open === o.id ? "Hide" : "Events"}</button>
                          </td>
                        </tr>
                        {open === o.id && (
                          <tr><td colSpan={9} style={{ padding: 0, borderBottom: `1px solid ${semanticColors.border}` }}>
                            <OrderEvents order={o} canRecord={false} onRecorded={() => undefined} />
                          </td></tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
